import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputMask from "../../../../u2000Core/components/formControls/input/U2000InputMask";
import U2000InputCurrency from "../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputDecimal from "../../../../u2000Core/components/formControls/input/U2000InputDecimal";
import type { TFunction } from "i18next";
import { NS } from "../../../U3101";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import { U2000FormStateEnumEditMode } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000CheckBoxArray from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxArray";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000InputCalendarCustom from "../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { EMPLOYEE_ROLE_TYPES } from "../../common/employeeRoleTypes";
import { isValidId } from "../../../../u2000Core/tools/U2000IdUtils";
import U2000RadioGroupString from "../../../../u2000Core/components/formControls/radio/U2000RadioGroupString";
import U2000InfoTooltip from "../../../../u2000Core/components/formControls/feedback/U2000InfoTooltip";
import U2000Label from "../../../../u2000Core/components/formControls/label/U2000Label";
import { useU3101FindAffiliationHandlers, useU3101FindCountriesHandlers, useU3101FindStatesHandlers } from "../../../U3101UseFindHandlers";

// Typage des props du composant
interface U3101EmployeeComponentProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
  /** BL-0442 : lecture seule si une FT approuvée existe. */
  remunerationReadOnly?: boolean;
}

function U3101EmployeeBody<T extends Record<string, any>>({
  formStateHandlers,
  t,
  remunerationReadOnly = false,
}: U3101EmployeeComponentProps<T>) {
  const { i18n } = useTranslation();
  const findCountriesHandlers = useU3101FindCountriesHandlers();
  const findStatesHandlers = useU3101FindStatesHandlers();
  const findAffiliationHandlers = useU3101FindAffiliationHandlers();

  const setStateSelection = useRef<((item: any) => void) | null>(null);
  const setStateInputValue = useRef<((value: string) => void) | null>(null);
  const previousCountryIdRef = useRef<string | null>(null);

  // Calcul dynamique des resultFields selon la langue pour les pays
  const countryResultFields = useMemo(() => {
    const language = i18n.language;
    if (language === "fr") return ["countryNameFr"];
    if (language === "en") return ["countryNameEn"];
    return ["countryName"]; // fallback pour rétrocompatibilité
  }, [i18n.language]);

  // Calcul dynamique des resultFields selon la langue pour les états
  const stateResultFields = useMemo(() => {
    const language = i18n.language;
    if (language === "fr") return ["stateNameFr"];
    if (language === "en") return ["stateNameEn"];
    return ["stateName"]; // fallback pour rétrocompatibilité
  }, [i18n.language]);

  const remunerationOptions = useMemo(
    () => [
      { value: "Salary", label: t("valRemunerationTypeSalary", { ns: NS }) },
      { value: "Hourly", label: t("valRemunerationTypeHourly", { ns: NS }) },
    ],
    [t],
  );

  // Fonction de recherche de pays
  const onSearchCountry = async (criterias: { fieldName: string; value: any }[]) => {
    return await findCountriesHandlers.search(criterias);
  };

  // Fonction d'affichage du pays
  const displayCountry = (item: any) => {
    if (!item) return "";
    const language = i18n.language;
    if (language === "fr" && item.countryNameFr) {
      return item.countryNameFr;
    }
    if (language === "en" && item.countryNameEn) {
      return item.countryNameEn;
    }
    // Fallback sur les anciens champs pour rétrocompatibilité
    return item.countryName || item.name || "";
  };

  const clearStateFields = [
    { fieldName: "state.id", value: null },
    { fieldName: "state.name", value: null },
    { fieldName: "state.stateName", value: null },
    { fieldName: "state.stateNameFr", value: null },
    { fieldName: "state.stateNameEn", value: null },
    { fieldName: "state.stateCode", value: null },
  ];

  const clearStateUi = () => {
    setStateSelection.current?.(null);
    setStateInputValue.current?.("");
  };

  // Fonction d'extraction des données du pays
  const extractCountryData = (result: any) => {
    clearStateUi();
    if (result) {
      const language = i18n.language;
      let countryName = "";
      if (language === "fr" && result.countryNameFr) {
        countryName = result.countryNameFr;
      } else if (language === "en" && result.countryNameEn) {
        countryName = result.countryNameEn;
      } else {
        // Fallback sur les anciens champs pour rétrocompatibilité
        countryName = result.countryName || result.name || "";
      }
      return [
        { fieldName: "country.id", value: result.id },
        { fieldName: "country.name", value: countryName },
        { fieldName: "country.countryName", value: countryName },
        { fieldName: "country.countryNameFr", value: result.countryNameFr || "" },
        { fieldName: "country.countryNameEn", value: result.countryNameEn || "" },
        { fieldName: "country.countryCode", value: result.countryCode || "" },
        ...clearStateFields,
      ];
    }
    return [
      { fieldName: "country.id", value: null },
      { fieldName: "country.name", value: null },
      { fieldName: "country.countryName", value: null },
      { fieldName: "country.countryNameFr", value: null },
      { fieldName: "country.countryNameEn", value: null },
      { fieldName: "country.countryCode", value: null },
      ...clearStateFields,
    ];
  };

  // Fonction de recherche de province/état
  const onSearchState = async (criterias: { fieldName: string; value: any }[]) => {
    const data = formStateHandlers.getData();
    const finalCriterias = [...criterias];
    if (data && data.country && isValidId(data.country.id)) {
      finalCriterias.push({ fieldName: "countryId", value: data.country.id.toString() });
    }
    return await findStatesHandlers.search(finalCriterias);
  };

  // Fonction d'affichage de la province/état
  const displayState = (item: any) => {
    if (!item) return "";
    const language = i18n.language;
    if (language === "fr" && item.stateNameFr) {
      return item.stateNameFr;
    }
    if (language === "en" && item.stateNameEn) {
      return item.stateNameEn;
    }
    // Fallback sur les anciens champs pour rétrocompatibilité
    return item.stateName || item.name || "";
  };

  // Fonction d'extraction des données de la province/état
  const extractStateData = (result: any) => {
    if (result) {
      const language = i18n.language;
      let stateName = "";
      if (language === "fr" && result.stateNameFr) {
        stateName = result.stateNameFr;
      } else if (language === "en" && result.stateNameEn) {
        stateName = result.stateNameEn;
      } else {
        // Fallback sur les anciens champs pour rétrocompatibilité
        stateName = result.stateName || result.name || "";
      }
      return [
        { fieldName: "state.id", value: result.id },
        { fieldName: "state.name", value: stateName },
        { fieldName: "state.stateName", value: stateName },
        // Sauvegarder aussi les champs multilingues pour que displayFn puisse fonctionner
        { fieldName: "state.stateNameFr", value: result.stateNameFr || "" },
        { fieldName: "state.stateNameEn", value: result.stateNameEn || "" },
        { fieldName: "state.stateCode", value: result.stateCode || "" },
      ];
    } else {
      return [
        { fieldName: "state.id", value: null },
        { fieldName: "state.name", value: null },
        { fieldName: "state.stateName", value: null },
        { fieldName: "state.stateNameFr", value: null },
        { fieldName: "state.stateNameEn", value: null },
        { fieldName: "state.stateCode", value: null },
      ];
    }
  };

  // Fonction de recherche d'affiliation
  const onSearchAffiliation = async (criterias: { fieldName: string; value: any }[]) => {
    return await findAffiliationHandlers.search(criterias);
  };

  const displayAffiliation = (item: any) => {
    if (!item) return "";
    return item.name ?? item.username ?? item.organizationUser ?? "";
  };

  const extractAffiliationData = (result: any) => {
    if (result) {
      return [
        { fieldName: "affiliation.id", value: result.id },
        { fieldName: "affiliation.name", value: result.name ?? "" },
        { fieldName: "affiliation.username", value: result.username ?? "" },
        { fieldName: "affiliation.organizationUser", value: result.organizationUser ?? "" },
      ];
    }
    return [
      { fieldName: "affiliation.id", value: null },
      { fieldName: "affiliation.name", value: null },
      { fieldName: "affiliation.username", value: null },
      { fieldName: "affiliation.organizationUser", value: null },
    ];
  };

  // Réinitialiser state quand country change ou est effacé
  useEffect(() => {
    const data = formStateHandlers.getData();
    const countryId = data?.country?.id || null;
    const currentCountryId = countryId && isValidId(countryId) ? countryId : null;
    const previousCountryId = previousCountryIdRef.current;

    // Ne réinitialiser que si :
    // 1. Le country a vraiment changé (et ce n'est pas le premier rendu où previousCountryId est null)
    // 2. OU si le country est effacé (currentCountryId est null mais il y avait un state)
    const countryChanged = previousCountryId !== null && previousCountryId !== currentCountryId;
    const countryCleared = previousCountryId !== null && currentCountryId === null;

    if ((countryChanged || countryCleared) && data && data.state && isValidId(data.state.id)) {
      const setNestedValue = (obj: any, path: string, value: any): any => {
        const keys = path.split(".");
        const cloned = JSON.parse(JSON.stringify(obj));
        let current = cloned;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return cloned;
      };

      const updatedData = setNestedValue(data, "state.id", null);
      const finalData = setNestedValue(updatedData, "state.name", null);
      const finalData2 = setNestedValue(finalData, "state.stateName", null);
      const finalData3 = setNestedValue(finalData2, "state.stateNameFr", null);
      const finalData4 = setNestedValue(finalData3, "state.stateNameEn", null);
      const finalData5 = setNestedValue(finalData4, "state.stateCode", null);
      formStateHandlers.setData(finalData5);
      setStateSelection.current?.(null);
      setStateInputValue.current?.("");
    }

    // Mettre à jour la référence du countryId précédent seulement après le premier rendu
    if (previousCountryId === null || previousCountryId !== currentCountryId) {
      previousCountryIdRef.current = currentCountryId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStateHandlers.getData()?.country?.id]);

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Première ligne : Identification (66 %) | Profil + Type de rémunération (34 %, côte à côte) */}
      <div className="flex items-stretch gap-4">
        {/* Colonne gauche (66%) */}
        <div className="flex flex-[66_66_0%] flex-col gap-4">
          {/* Panel Identification */}
          <U2000Panel title={t("lblIdentification", { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3 flex w-full flex-wrap items-start gap-4">
              <div className="min-w-[140px] flex-1">
                <U2000InputText
                  fieldName="codeEmployee"
                  label={t("lblCodeEmployee", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  readonly={formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
                />
              </div>
              <div className="min-w-[140px] flex-1">
                <U2000InputText fieldName="numEmployee" label={t("lblNumEmployee", { ns: NS })} formStateHandlers={formStateHandlers} required={true} />
              </div>
              <div className="flex min-w-[220px] flex-[1.25] flex-row flex-wrap items-start gap-4">
                <div className="min-w-[160px] min-h-0 flex-1">
                  <U2000AutoCompleteInput
                    formStateHandlers={formStateHandlers}
                    fieldName="affiliation"
                    mainFieldName="name"
                    label={t("lblAffiliation", { ns: NS })}
                    onSearch={onSearchAffiliation}
                    displayFn={displayAffiliation}
                    overrideExtractDataFn={extractAffiliationData}
                    minCarLaunchSearch={0}
                    resultLabels={[]}
                    resultFields={["name", "username", "organizationUser"]}
                    required={false}
                  />
                </div>
                {/* Même empilement que le champ à gauche (U2000Label + space-y-1.5 + zone 46px) : pas de mt arbitraire */}
                <div className="flex shrink-0 flex-col space-y-1.5">
                  <div className="invisible pointer-events-none select-none" aria-hidden>
                    <U2000Label label={t("lblAffiliation", { ns: NS })} required={false} htmlFor="u3101-isActive-align" />
                  </div>
                  <div className="flex h-[34px] items-center">
                    <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="isActive" label={t("lblIsActive", { ns: NS })} />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 flex w-full flex-wrap items-start gap-4">
              <div className="min-w-[120px] flex-1">
                <U2000InputText fieldName="firstName" label={t("lblFirstName", { ns: NS })} formStateHandlers={formStateHandlers} required={true} />
              </div>
              <div className="min-w-[120px] flex-1">
                <U2000InputText fieldName="lastName" label={t("lblLastName", { ns: NS })} formStateHandlers={formStateHandlers} required={true} />
              </div>
              <div className="w-38 shrink-0">
                <U2000InputCalendarCustom
                  formStateHandlers={formStateHandlers}
                  fieldName="birthDate"
                  label={t("lblBirthDate", { ns: NS })}
                  dateFormat="yyyy-MM-dd"
                  required={false}
                />
              </div>
              {/* Aligné sur la zone input (pas sur les libellés) : même empilement label invisible + h-[34px] */}
              <div className="flex shrink-0 flex-col space-y-1.5">
                <div className="invisible pointer-events-none select-none" aria-hidden>
                  <U2000Label label={t("lblBirthDate", { ns: NS })} required={false} htmlFor="u3101-hideBirthday-align" />
                </div>
                <div className="flex h-[34px] items-center">
                  <U2000CheckBox
                    formStateHandlers={formStateHandlers}
                    fieldName="hideBirthdayInCalendar"
                    label={t("lblHideBirthdayInCalendar", { ns: NS })}
                  />
                </div>
              </div>
            </div>
          </U2000Panel>

        </div>

        {/* Colonne droite (34 %) : Profil | Type de rémunération */}
        <div className="flex min-w-0 flex-[34_34_0%] flex-row items-stretch gap-4">
          <U2000Panel title={t("lblProfile", { ns: NS })} titleAlign="left" className="flex min-w-0 flex-1 flex-col">
            <div className="mt-3 flex w-full flex-col gap-y-2 px-2">
              {EMPLOYEE_ROLE_TYPES.map((type) => (
                <U2000CheckBoxArray
                  key={`operation-type-${type.value}`}
                  fieldName="operationType"
                  value={type.value}
                  label={typeof type.label === "object" ? (i18n.language === "fr" ? type.label.fr : type.label.en) : String(type.label)}
                  formStateHandlers={formStateHandlers}
                />
              ))}
            </div>
          </U2000Panel>

          <U2000Panel title={t("lblRemunerationType", { ns: NS })} titleAlign="left" className="relative flex min-w-0 flex-1 flex-col">
            {remunerationReadOnly ? (
              <div className="absolute right-3 top-3 z-10">
                <U2000InfoTooltip
                  icon="question"
                  align="right"
                  iconClassName="h-4 w-4 text-gray-500 hover:text-blue-600"
                  message={t("hintRemunerationReadOnlyTooltip", { ns: NS })}
                />
              </div>
            ) : null}
            <div className="mt-3 grid grid-cols-2 gap-4 px-2">
              <div className="flex flex-col gap-2">
                <U2000RadioGroupString
                  orientation="vertical"
                  formStateHandlers={formStateHandlers}
                  fieldName="remunerationType"
                  options={remunerationOptions}
                  required={true}
                  readonly={remunerationReadOnly}
                />
              </div>
              <div className="flex flex-col gap-2">
                <U2000InputCurrency
                  fieldName="hourRate"
                  label={t("lblHourRate", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={false}
                  decimalDigits={2}
                  readonly={remunerationReadOnly}
                  disabled={remunerationReadOnly}
                />
                <U2000InputDecimal
                  fieldName="hourlyBase"
                  label={t("lblHourlyBase", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={false}
                  allowNegative={false}
                  decimalDigits={2}
                  textAlign="right"
                  readonly={remunerationReadOnly}
                />
              </div>
            </div>
          </U2000Panel>
        </div>
      </div>

      {/* Ligne Adresse (66%) + Heures BNC (34%) */}
      <div className="flex items-stretch gap-4">
        {/* Colonne gauche (66%) - Adresse */}
        <div className="flex flex-[66_66_0%] flex-col gap-4">
          <U2000Panel title={t("lblAddress", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col">
            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000InputText fieldName="addressLine1" label={t("lblAddressLine1", { ns: NS })} formStateHandlers={formStateHandlers} required={true} />
              </div>
            </div>

            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000InputText fieldName="city" label={t("lblCity", { ns: NS })} formStateHandlers={formStateHandlers} required={true} />
              </div>
              <div className="flex-1">
                <U2000InputMask fieldName="postalCode" label={t("lblPostalCode", { ns: NS })} mask="postalCodeCA" formStateHandlers={formStateHandlers} required={true} />
              </div>
            </div>

            <div className="mt-3 flex w-full gap-4">
              <div className="flex-1">
                <U2000AutoCompleteInput
                  formStateHandlers={formStateHandlers}
                  fieldName="country"
                  mainFieldName="countryName"
                  label={t("lblCountry", { ns: NS })}
                  onSearch={onSearchCountry}
                  displayFn={displayCountry}
                  overrideExtractDataFn={extractCountryData}
                  minCarLaunchSearch={0}
                  resultLabels={[]}
                  resultFields={countryResultFields}
                  required={true}
                />
              </div>
              <div className="flex-1">
                <U2000AutoCompleteInput
                  formStateHandlers={formStateHandlers}
                  fieldName="state"
                  mainFieldName="stateName"
                  label={t("lblState", { ns: NS })}
                  onSearch={onSearchState}
                  displayFn={displayState}
                  overrideExtractDataFn={extractStateData}
                  minCarLaunchSearch={0}
                  resultLabels={[]}
                  resultFields={stateResultFields}
                  readonly={!formStateHandlers.getData()?.country?.id || formStateHandlers.getData()?.country?.id === ""}
                  required={true}
                  onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
                    setStateSelection.current = setCurrentSelection;
                    setStateInputValue.current = setInputValue;
                  }}
                />
              </div>
            </div>
          </U2000Panel>
        </div>

        {/* Colonne droite (34%) - Heures BNC, même largeur que Statut et Profil */}
        <div className="flex flex-[34_34_0%] flex-col gap-4">
          <U2000Panel title={t("lblBncHours", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col">
            <div className="mt-3 flex flex-1 flex-col gap-2 px-2">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.75fr)] gap-2">
                <U2000InputDecimal
                  fieldName="vacationBncHoursInitial"
                  label={t("lblVacationBncHoursInitial", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={false}
                  decimalDigits={2}
                />
                <U2000InputDecimal
                  fieldName="vacationBncHours"
                  label={t("lblVacationBncHours", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={true}
                  decimalDigits={2}
                />
                <U2000InputDecimal
                  fieldName="vacationNegativeThresholdHours"
                  label={t("lblVacationNegativeThresholdHours", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={false}
                  decimalDigits={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <U2000InputDecimal
                  fieldName="holidayBncHoursInitial"
                  label={t("lblHolidayBncHoursInitial", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={false}
                  decimalDigits={2}
                />
                <U2000InputDecimal
                  fieldName="holidayBncHours"
                  label={t("lblHolidayBncHours", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={true}
                  decimalDigits={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <U2000InputDecimal
                  fieldName="sickLeaveBncHoursInitial"
                  label={t("lblSickLeaveBncHoursInitial", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={false}
                  decimalDigits={2}
                />
                <U2000InputDecimal
                  fieldName="sickLeaveBncHours"
                  label={t("lblSickLeaveBncHours", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={true}
                  decimalDigits={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <U2000InputDecimal
                  fieldName="specialBncHoursInitial"
                  label={t("lblSpecialBncHoursInitial", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={false}
                  decimalDigits={2}
                />
                <U2000InputDecimal
                  fieldName="specialBncHours"
                  label={t("lblSpecialBncHours", { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required={true}
                  allowNegative={true}
                  decimalDigits={2}
                />
              </div>
            </div>
          </U2000Panel>
        </div>
      </div>

      {/* Panel Coordonnées - Pleine page */}
      <U2000Panel title={t("lblContactInfo", { ns: NS })} titleAlign="left" className="w-full">
        <div className="mt-3 flex w-full gap-4">
          <div className="flex-1">
            <U2000InputText fieldName="email" label={t("lblEmail", { ns: NS })} type="email" formStateHandlers={formStateHandlers} required={false} />
          </div>
        </div>

        <div className="mt-3 flex w-full gap-4">
          <div className="flex-1">
            <U2000InputMask fieldName="phoneNumber" label={t("lblPhoneNumber", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} required={false} />
          </div>
          <div className="flex-1">
            <U2000InputMask fieldName="cellPhone" label={t("lblCellPhone", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} />
          </div>
        </div>

        <div className="mt-3 flex w-full gap-4">
          <div className="flex-1">
            <U2000InputMask fieldName="otherPhone" label={t("lblOtherPhone", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} />
          </div>
          <div className="w-32">
            <U2000InputText fieldName="otherPhoneExt" label={t("lblOtherPhoneExt", { ns: NS })} formStateHandlers={formStateHandlers} />
          </div>
          <div className="flex-3">
            <U2000InputText fieldName="otherPhoneComment" label={t("lblOtherPhoneComment", { ns: NS })} formStateHandlers={formStateHandlers} />
          </div>
        </div>
      </U2000Panel>
    </div>
  );
}

export default U3101EmployeeBody;
